import React, { Fragment } from 'react';
import { Checkbox, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { Collapse } from 'src/components/Collapse';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { ReactComponent as CheckboxOffIcon } from 'src/icons/checkboxOff.svg';
import { ReactComponent as CheckboxOnIcon } from 'src/icons/checkboxOn.svg';
import palette from 'src/theme/palette';
import {
  ConnectorType,
  Metric,
  MetricsByConnectorType,
} from 'src/utils/gql/types';

import {
  facebookRegionSplitKey,
  incompatibleRegionSplittingMetricKeys,
  METRICS_MAX,
} from '../constants';
import {
  isDatabaseFieldJoinCompatible,
  shouldShowDatabaseField,
} from '../../DatabaseConnector/databaseJoinCompatibility';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import {
  checkboxStyles,
  Container,
  InnerContainer,
  MetricName,
  metricNameStyles,
  MetricWrapper,
  SubGroupName,
} from '../styled';
import { DashboardForm, InputName, MetricsDropdownProps } from '../types';
import { sortArrayBySecondArray } from '../utils';

export const MetricsDropdown = ({
  type,
  label,
  icon,
  expanded,
  metricsByCategory,
  loading,
  onToggle,
  onClickMetric,
  debFunction,
  showConfigureButton,
}: MetricsDropdownProps) => {
  const toggleCollapse = () => onToggle(type);
  const navigate = useNavigate();
  const { databaseJoinEdges = [], databaseMainTables = [] } = useConnectors();

  const { control, setValue, watch, getValues } =
    useFormContext<DashboardForm>();

  const handleConfigure = (type: ConnectorType | MetricsByConnectorType) => {
    navigate(
      `../${ROUTE_SOURCES.CONNECTORS}/${ROUTE_SOURCES.ACCOUNTS}?type=${type}`,
      { replace: true },
    );
  };

  const checkDisabledCategory = (
    value: Metric[],
    category: string,
    metrics: Metric[],
  ) => {
    return (
      (watch('metrics').length >= METRICS_MAX &&
        !checkCategoryCheckbox(value, category, metrics)) ||
      watch('metrics').some(
        ({ incompatibleMetrics }) =>
          incompatibleMetrics &&
          incompatibleMetrics?.length > 0 &&
          value[0].type === ConnectorType.GOOGlE_ANALYTICS4,
      )
    );
  };

  const handleCategoryClick = (
    value: Metric[],
    category: string,
    metrics: Metric[],
  ) => {
    if (checkDisabledCategory(value, category, metrics)) return;

    const valuesWithThisCategory = value.filter(
      (metric) => metric.category === category && metric.type === type,
    );

    if (valuesWithThisCategory.length === metrics.length) {
      if (value.length === valuesWithThisCategory.length) {
        debFunction?.();

        setValue(InputName.metrics, value.slice(0, 1));
      } else {
        debFunction?.();

        setValue(
          InputName.metrics,
          value.filter(
            (metric) => !(metric.type === type && metric.category === category),
          ),
        );
      }
    } else {
      const valueIds = value.map(({ id }) => id);

      const newMetrics = metrics.filter(({ id }) => !valueIds.includes(id));
      const incompatibleMetric = metrics.find(
        (metric) =>
          metric?.incompatibleMetrics &&
          metric?.incompatibleMetrics?.length > 0,
      )?.incompatibleMetrics;
      const result = newMetrics.filter((metric) =>
        incompatibleMetric?.some((key) => key === metric.key),
      );
      debFunction?.();

      setValue(InputName.metrics, [...value, ...result].slice(0, METRICS_MAX));
    }
  };

  const checkCategoryCheckbox = (
    value: Metric[],
    category: string,
    metrics: Metric[],
  ) => {
    const valuesWithThisCategory = value.filter(
      (metric) => metric.category === category && metric.type === type,
    );

    return valuesWithThisCategory.length === metrics.length;
  };

  const getCollapseCount = (value: Metric[]) => {
    const valuesWithThisCategory = value.filter(
      (metric) => metric.type === type,
    );

    return valuesWithThisCategory.length;
  };

  const checkDisableOption = (metric: Metric) => {
    const metrics = getValues(InputName.metrics);

    if (metrics.some(({ id }) => id === metric.id)) {
      return metrics.length === 1;
    } else if (metrics.length >= METRICS_MAX) {
      return true;
    }

    const incompatibleMetricWithMetrics = metrics.some(
      ({ incompatibleMetrics }) =>
        incompatibleMetrics &&
        incompatibleMetrics?.length > 0 &&
        incompatibleMetrics.find((value) => value === metric.key),
    );

    const splits = getValues(InputName.splits);

    const incompatibleMetricWithSplits =
      metric.type === ConnectorType.FACEBOOK &&
      incompatibleRegionSplittingMetricKeys.has(metric.key) &&
      splits.some(
        ({ key, connectorType }) =>
          connectorType === ConnectorType.FACEBOOK &&
          key === facebookRegionSplitKey,
      );

    const dbSplitKeys = splits
      .filter(({ connectorType }) => connectorType === ConnectorType.DATABASE)
      .map(({ key }) => key);
    const dbMetricKeys = metrics
      .filter(({ type }) => type === ConnectorType.DATABASE)
      .map(({ key }) => key);

    const incompatibleDatabaseJoin =
      metric.type === ConnectorType.DATABASE &&
      dbSplitKeys.length > 0 &&
      !isDatabaseFieldJoinCompatible(
        metric.key,
        dbSplitKeys,
        dbMetricKeys,
        databaseJoinEdges,
        databaseMainTables,
      );

    return (
      incompatibleMetricWithMetrics ||
      incompatibleMetricWithSplits ||
      incompatibleDatabaseJoin
    );
  };

  const isDatabaseMetricVisible = (metric: Metric) => {
    if (metric.type !== ConnectorType.DATABASE) return true;

    const splits = getValues(InputName.splits);
    const metrics = getValues(InputName.metrics);
    const dbSplitKeys = splits
      .filter(({ connectorType }) => connectorType === ConnectorType.DATABASE)
      .map(({ key }) => key);
    const dbMetricKeys = metrics
      .filter(({ type }) => type === ConnectorType.DATABASE)
      .map(({ key }) => key);

    return shouldShowDatabaseField(
      metric.key,
      dbSplitKeys,
      dbMetricKeys,
      databaseJoinEdges,
      databaseMainTables,
      'metric',
    );
  };

  const handleMetricClick = (metric: Metric) => {
    if (checkDisableOption(metric)) {
      return;
    }

    onClickMetric?.(metric);
  };

  return (
    <Controller
      name={InputName.metrics}
      control={control}
      render={({ field: { value } }) => (
        <Collapse
          count={getCollapseCount(value)}
          title={label}
          icon={icon}
          expanded={expanded}
          onToggle={toggleCollapse}
          loading={loading}
        >
          <Container pt={showConfigureButton ? '10px' : ''}>
            {showConfigureButton && !loading ? (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={() => handleConfigure(type)}
              >
                Configure
              </Button>
            ) : (
              <>
                {!Object.entries(metricsByCategory).length && !loading && (
                  <Typography m="auto">No metrics</Typography>
                )}
                {/* <MetricWrapper ml="8px" mt="10px">
              <Checkbox
                sx={checkboxStyles}
                icon={<CheckboxOffIcon height={24} width={24} />}
                checkedIcon={<CheckboxOnIcon height={24} width={24} />}
                // checked={value.some((item) => item.id === metric.id)}
                disableRipple
              />
              <MetricName>Total Sales</MetricName>
            </MetricWrapper> */}
                {!loading &&
                  Object.entries(metricsByCategory).map(
                    ([category, metrics], index) => (
                      <Fragment key={index}>
                        <MetricWrapper
                          onClick={() =>
                            handleCategoryClick(value, category, metrics)
                          }
                        >
                          <Checkbox
                            sx={checkboxStyles}
                            icon={<CheckboxOffIcon height={24} width={24} />}
                            checkedIcon={
                              <CheckboxOnIcon height={24} width={24} />
                            }
                            checked={checkCategoryCheckbox(
                              value,
                              category,
                              metrics,
                            )}
                            disableRipple
                            disabled={checkDisabledCategory(
                              value,
                              category,
                              metrics,
                            )}
                          />
                          <SubGroupName
                            sx={metricNameStyles(
                              checkDisabledCategory(value, category, metrics),
                            )}
                            variant="body2"
                          >
                            {category}
                          </SubGroupName>
                        </MetricWrapper>

                        <InnerContainer>
                          {sortArrayBySecondArray(metrics, value)
                            .filter(isDatabaseMetricVisible)
                            .map(
                            (metric, index) => (
                              <MetricWrapper
                                key={index}
                                onClick={() => handleMetricClick(metric)}
                              >
                                <Checkbox
                                  sx={checkboxStyles}
                                  icon={
                                    <CheckboxOffIcon height={24} width={24} />
                                  }
                                  checkedIcon={
                                    <CheckboxOnIcon
                                      height={24}
                                      width={24}
                                      color={
                                        checkDisableOption(metric)
                                          ? palette.action.disabled
                                          : undefined
                                      }
                                    />
                                  }
                                  checked={value.some(
                                    (item) => item.id === metric.id,
                                  )}
                                  disabled={checkDisableOption(metric)}
                                  disableRipple
                                />
                                <MetricName
                                  sx={metricNameStyles(
                                    checkDisableOption(metric),
                                  )}
                                >
                                  {metric.label}
                                </MetricName>
                              </MetricWrapper>
                            ),
                          )}
                        </InnerContainer>
                      </Fragment>
                    ),
                  )}
              </>
            )}
          </Container>
        </Collapse>
      )}
    />
  );
};
